import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import { Plus, RefreshCw } from "lucide-react";
import { buttonVariants } from "@/components/ui/button";
import { Table, TableWrapper } from "@/components/ui/table";
import { FilterBar, FilterSearch, FilterSelect } from "@/components/data-display/filter-bar";
import { StatCard } from "@/components/data-display/stat-card";
import { WalletRow } from "@/features/wallets/wallet-table";
import { useWallets } from "@/hooks/use-wallets";
import { PermissionGate } from "@/components/permission/permission-gate";
import type { Wallet } from "@/types";

const usdCompact = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  maximumFractionDigits: 1,
});

function walletHref(w: Wallet): string {
  return `/wallets/${w.id}`;
}

const ALL_CHAINS = "All chains";
const ALL_PROVIDERS = "All Providers";
const ALL_TYPES = "All Types";
const ALL_STATUSES = "All Statuses";

export default function WalletsPage() {
  const { wallets, loading, error, refresh } = useWallets();
  const [query, setQuery] = useState("");
  const [chain, setChain] = useState(ALL_CHAINS);
  const [provider, setProvider] = useState(ALL_PROVIDERS);
  const [type, setType] = useState(ALL_TYPES);
  const [status, setStatus] = useState(ALL_STATUSES);

  const filterOptions = useMemo(() => {
    const chains = new Set<string>();
    const providers = new Set<string>();
    const types = new Set<string>();
    const statuses = new Set<string>();

    for (const w of wallets) {
      if (w.chain) chains.add(w.chain);
      if (w.provider) providers.add(w.provider);
      if (w.type) types.add(w.type.toUpperCase());
      if (w.status) statuses.add(w.status.charAt(0).toUpperCase() + w.status.slice(1));
    }

    const toOptions = (all: string, set: Set<string>) => [
      { value: all, label: all },
      ...[...set].sort().map((v) => ({ value: v, label: v })),
    ];

    return {
      chains: toOptions(ALL_CHAINS, chains),
      providers: toOptions(ALL_PROVIDERS, providers),
      types: toOptions(ALL_TYPES, types),
      statuses: toOptions(ALL_STATUSES, statuses),
    };
  }, [wallets]);

  const rows = useMemo(() => {
    return wallets.filter((w) => {
      const q = query.toLowerCase();
      const matchesQ = !q || `${w.name} ${w.id} ${w.provider}`.toLowerCase().includes(q);
      const matchesChain = chain === ALL_CHAINS || w.chain === chain;
      const matchesProvider = provider === ALL_PROVIDERS || w.provider === provider;
      const matchesType = type === ALL_TYPES || w.type === type.toLowerCase();
      const matchesStatus = status === ALL_STATUSES || w.status === status.toLowerCase();
      return matchesQ && matchesChain && matchesProvider && matchesType && matchesStatus;
    });
  }, [wallets, query, chain, provider, type, status]);

  const stats = useMemo(() => {
    const pending = wallets.filter((w) => w.status === "pending").length;
    const totalAum = wallets.reduce((sum, w) => sum + (w.balanceUsd ?? 0), 0);
    const chains = new Set(wallets.map((w) => w.chain)).size;
    const providers = new Set(wallets.map((w) => w.provider)).size;
    return [
      { value: String(wallets.length), label: "Total Wallets", hint: pending ? `+${pending} pending` : null, positiveHint: true },
      { value: totalAum > 0 ? usdCompact.format(totalAum) : "$0", label: "Total AUM", hint: null, positiveHint: false },
      { value: String(chains), label: "Chains Active", hint: null, positiveHint: false },
      { value: String(providers), label: "Providers", hint: null, positiveHint: false },
    ];
  }, [wallets]);

  return (
    <div>
      {!loading && !error && (
        <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {stats.map((s) => (
            <StatCard key={s.label} {...s} />
          ))}
        </div>
      )}

      <FilterBar className="mb-5">
        <FilterSearch value={query} onChange={setQuery} placeholder="Search wallets by label, chain, provider…" />
        <FilterSelect value={chain} onChange={setChain} options={filterOptions.chains} ariaLabel="Filter by chain" />
        <FilterSelect value={provider} onChange={setProvider} options={filterOptions.providers} ariaLabel="Filter by provider" />
        <FilterSelect value={type} onChange={setType} options={filterOptions.types} ariaLabel="Filter by type" />
        <FilterSelect value={status} onChange={setStatus} options={filterOptions.statuses} ariaLabel="Filter by status" />
        <button
          type="button"
          onClick={refresh}
          className="inline-flex items-center gap-1.5 rounded-md border border-line bg-surface px-2.5 py-1.5 text-xs font-medium text-muted hover:text-ink"
          aria-label="Refresh wallets"
        >
          <RefreshCw className="size-3.5" aria-hidden />
        </button>
        <PermissionGate permission="wallet:create">
          <Link to="/wallets/new" className={buttonVariants({ size: "md" })}>
            <Plus className="size-4" aria-hidden /> Create Wallet
          </Link>
        </PermissionGate>
      </FilterBar>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="spinner" aria-label="Loading wallets" />
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-danger-border bg-danger-soft px-4 py-5 text-sm text-danger">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="font-semibold text-ink">Failed to load wallets</div>
              <div className="mt-1">
                Reason <code className="rounded-sm bg-surface px-1.5 py-0.5 text-xs text-danger">{error.reasonCode}</code>
              </div>
              {error.supportReference && (
                <div className="mt-1">
                  Support ref <code className="rounded-sm bg-surface px-1.5 py-0.5 text-xs text-danger">{error.supportReference}</code>
                </div>
              )}
            </div>
            <button type="button" onClick={refresh} className="shrink-0 underline">
              Retry
            </button>
          </div>
        </div>
      )}

      {!loading && !error && (
        <TableWrapper>
          <Table className="dense">
            <colgroup>
              <col style={{ width: 160 }} />
              <col style={{ width: 160 }} />
              <col style={{ width: 130 }} />
              <col style={{ width: 56 }} />
              <col style={{ width: 140 }} />
              <col style={{ width: 90 }} />
              <col style={{ width: 96 }} />
              <col style={{ width: 44 }} />
            </colgroup>
            <thead>
              <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
                <th className="px-4 py-2.5">Wallet</th>
                <th className="px-4 py-2.5">Chain</th>
                <th className="px-4 py-2.5">Provider</th>
                <th className="px-4 py-2.5">Type</th>
                <th className="px-4 py-2.5">Balance (USD)</th>
                <th className="px-4 py-2.5">Source</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5 w-10" aria-label="Row actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((w) => (
                <WalletRow key={w.id} wallet={w} href={walletHref(w)} />
              ))}
            </tbody>
          </Table>
          {rows.length === 0 && (
            <div className="px-4 py-10 text-center text-sm text-muted">
              {wallets.length === 0 ? "No wallets yet. Create your first wallet to get started." : "No wallets match your filters."}
            </div>
          )}
        </TableWrapper>
      )}
    </div>
  );
}
